import { type ReactNode } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router';
import { api, brl, int, WORKER_APP_URL } from '../api';
import { useMeRequired } from '../me';
import { Wordmark } from './Brand';
import { ScopePicker } from './ScopePicker';
import { ScopeProvider } from '../scope';
import { Mic, Image as ImageIcon, Sparkles, LayoutDashboard, Settings2, LogOut, KeyRound, BarChart3, Bot, FileText, HardDrive, ExternalLink, Building2, Library as LibraryIcon, FileBarChart } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const NAV_CHAT = { to: '/chat', label: 'Chat', icon: Sparkles, end: false };
const NAV_HOME = { to: '/', label: 'Início', icon: LayoutDashboard, end: true };
const NAV_IA = [
  { to: '/audio', label: 'Áudio', icon: Mic },
  { to: '/imagem', label: 'Imagem', icon: ImageIcon },
  { to: '/agentes', label: 'Agentes', icon: Bot },
  { to: '/documentos', label: 'Documentos', icon: FileText },
  { to: '/biblioteca', label: 'Biblioteca', icon: LibraryIcon },   // sem aba no mobile (a barra já tem 7); o acesso é por aqui e por /documentos
];
// Gestão = listas e ações; painéis e relatórios (Início, Uso e créditos, Relatórios da empresa) ficam em "Visão geral".
const NAV_GESTAO = [
  { to: '/chaves', label: 'Chaves de API', icon: KeyRound },
  { to: '/configuracoes', label: 'Configurações', icon: Settings2 },
];
const NAV_USO = { to: '/uso', label: 'Uso e créditos', icon: BarChart3 };
const TABS = [
  NAV_CHAT,
  NAV_HOME,
  { to: '/agentes', label: 'Agentes', icon: Bot },
  { to: '/documentos', label: 'Docs', icon: FileText },
  { to: '/chaves', label: 'Chaves', icon: KeyRound },
  { to: '/uso', label: 'Uso', icon: BarChart3 },
  { to: '/configuracoes', label: 'Conta', icon: Settings2 },
];

/** A casca: o escopo do chat (onde processa) vale para o seletor da barra lateral, para o chip do mobile e para as páginas. */
export function Shell({ children }: { children: ReactNode }) {
  return <ScopeProvider><ShellBody>{children}</ShellBody></ScopeProvider>;
}

function ShellBody({ children }: { children: ReactNode }) {
  const { me, setMe } = useMeRequired();
  const nav = useNavigate();
  const loc = useLocation();

  const logout = async (e: React.MouseEvent) => {
    e.preventDefault();
    await api('/api/client/logout', { method: 'POST' });
    setMe(null);
    nav('/entrar');
  };

  const live = me.nodes > 0;
  // telas de conversa ocupam a altura toda sem padding (modo chat): o chat comum e a conversa de um agente (/agentes/<id>[/<conversa>]), não a lista de agentes
  const inChat = loc.pathname.startsWith('/chat') || loc.pathname.startsWith('/imagem') || /^\/agentes\/[^/]+/.test(loc.pathname);   // telas de conversa: altura fixa, a lista rola e o campo fica no rodapé

  return (
    <div className="shell">
      <aside className="rail">
        <div className="brand"><Wordmark size="sm" /></div>
        <div className="scope"><ScopePicker /></div>

        <div style={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column' }}>
          <div className="nav">
            <NavItem {...NAV_CHAT} />
          </div>
          <div className="nav">
            <div className="nav-sec-title">Visão geral</div>
            <NavItem {...NAV_HOME} />
            <NavItem {...NAV_USO} />
            {me.org?.role === 'admin' && <NavItem to="/empresa/relatorios" label="Relatórios da empresa" icon={FileBarChart} />}
          </div>
          <div className="nav">
            <div className="nav-sec-title">Ferramentas IA</div>
            {NAV_IA.map((item) => <NavItem key={item.to} {...item} />)}
          </div>
          <div className="nav">
            <div className="nav-sec-title">Gestão</div>
            {NAV_GESTAO.map((item) => <NavItem key={item.to} {...item} />)}
            {me.org?.role === 'admin' && <NavItem to="/empresa" label="Empresa" icon={Building2} end />}
            <a href={WORKER_APP_URL}><HardDrive /><span>Máquinas (Hub)</span><ExternalLink className="ext" /></a>
          </div>
        </div>

        <div className="rail-foot">
          <div className={`rail-status ${live ? 'live' : ''}`}><i /><span>{live ? `${int(me.nodes)} nós na malha` : 'malha sem nós'}</span></div>
          <div className="balance-card">
            <span className="lbl">{me.creditsScope === 'org' ? 'Créditos da empresa' : 'Créditos disponíveis'}</span>
            <span className="val">{brl(me.credits, 2)}</span>
            {me.creditsScope === 'org' && me.credits <= 0 && <span className="bal-note">Sem créditos da empresa: peça uma recarga ao administrador.</span>}
            {me.limit && (
              <span className="bal-limit" title={`Seu limite: ${brl(me.limit.spent, 2)} de ${brl(me.limit.amount, 2)} este mês`}>
                <i style={{ width: `${me.limit.amount > 0 ? Math.min(100, (me.limit.spent / me.limit.amount) * 100) : 100}%` }} />
                <em>seu limite: {brl(me.limit.spent, 2)} de {brl(me.limit.amount, 2)} este mês</em>
              </span>
            )}
          </div>
          {me.org && <div className="org-name" title={me.org.name}>{me.org.name}</div>}
          <div className="user-row">
            <span title={me.email}>{me.email}</span>
            <LogOut onClick={logout} aria-label="Sair" role="button" />
          </div>
        </div>
      </aside>

      <header className="topbar">
        <Wordmark size="sm" />
        <ScopePicker variant="chip" />
        <span className="bal">{brl(me.credits, 2)}</span>
      </header>

      <main className={`main ${inChat ? 'chat-mode' : ''}`}>
        <AnimatePresence mode="wait">
          <motion.div
            key={loc.pathname}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.15, ease: 'easeOut' }}
            style={{ minHeight: '100%', width: '100%' }}
          >
            {children}
          </motion.div>
        </AnimatePresence>
      </main>

      <nav className="tabbar" aria-label="Navegação principal">
        {TABS.map((t) => (
          <NavLink key={t.to} to={t.to} end={'end' in t ? t.end : undefined} className={({ isActive }) => (isActive ? 'on' : '')}>
            <t.icon /><span>{t.label}</span>
          </NavLink>
        ))}
      </nav>
    </div>
  );
}

function NavItem({ to, icon: Icon, label, end }: { to: string; icon: React.ComponentType; label: string; end?: boolean }) {
  return (
    <NavLink to={to} end={end} className={({ isActive }) => (isActive ? 'on' : '')}>
      <Icon />
      <span>{label}</span>
    </NavLink>
  );
}
